'use client';

import { FC, PropsWithChildren, ButtonHTMLAttributes } from 'react';
import { type ValueOf } from '../../types/types';
import { AppRoute } from '../../enums/enums';
import { cn } from '@shared/shared-utils';
import './button.scss';
import Link from 'next/link';

type Properties = {
  to?: ValueOf<typeof AppRoute> | string;
  target?: '_blank' | '_self';
  className?: string;
  linkClassName?: string;
  variant?: 'yellow' | 'white' | 'blue' | 'blue-white';
  size?: 'sm' | 'md' | 'lg';
  fancy?: boolean;
} & PropsWithChildren &
  ButtonHTMLAttributes<HTMLButtonElement>;

const Button: FC<Properties> = ({
  to,
  className,
  children,
  variant = 'yellow',
  size = 'md',
  target = '_self',
  fancy = true,
  linkClassName,
  ...rest
}: Properties) => {
  const buttonType = rest.type ?? 'button';

  const buttonContent = (
    <button
      type={buttonType}
      className={cn(
        'px-4 py-1 rounded-3xl text-[14px] lg:text-xl font-bold border-0',
        fancy && 'fancy-fill-btn',
        variant === 'yellow' && 'bg-ogn-bright-yellow text-ogn-blue',
        variant === 'white' && 'bg-white text-ogn-blue',
        variant === 'blue' && 'bg-ogn-blue text-white',
        size === 'sm' && 'px-2 py-1 text-[14px]',
        className,
      )}
      data-btn-variant={variant}
      {...rest}
    >
      {children}
    </button>
  );

  return to ? (
    <Link href={to} target={target} className={linkClassName}>
      {buttonContent}
    </Link>
  ) : (
    buttonContent
  );
};

export { Button };
